import {
  categorizeRegisterError,
  contactHint,
  errorCodeFromDetail,
  persistHint,
  RegisterErrorCategory,
  RegisterErrorHint
} from '@bo-client/activation-management/shared/register-error-hint';

/**
 * Übersetzt einen Fehler der MaStR-Abfrage (`/external/mastr-data`) in einen deutschsprachigen
 * Hinweistext für die Anzeige in der Fehler-Hinweiskarte.
 *
 * <p>Code-Extraktion, Kategorisierung und die rollenabhängigen Handlungshinweise sind für alle
 * Registerabfragen gleich und liegen in `register-error-hint.ts`; hier stehen nur die MaStR-Codes
 * und die MaStR-Texte je Kategorie.
 */

/** Stabile Maschinen-Codes, die das Backend im `detail`-Feld voranstellt. */
export type MastrErrorCode =
  | 'MASTR_INVALID_INPUT'
  | 'MASTR_API_KEY_MISSING'
  | 'MASTR_INVALID_API_KEY'
  | 'MASTR_NOT_FOUND'
  | 'MASTR_NOT_CONFIGURED'
  | 'MASTR_UNREACHABLE'
  | 'MASTR_TIMEOUT'
  | 'MASTR_ERROR';

export type MastrErrorHint = RegisterErrorHint;

const MASTR_ERROR_CATEGORIES: Record<MastrErrorCode, RegisterErrorCategory> = {
  MASTR_NOT_FOUND: 'notFound',
  MASTR_INVALID_INPUT: 'invalidInput',
  MASTR_API_KEY_MISSING: 'configuration',
  MASTR_INVALID_API_KEY: 'configuration',
  MASTR_NOT_CONFIGURED: 'configuration',
  MASTR_UNREACHABLE: 'transient',
  MASTR_TIMEOUT: 'transient',
  MASTR_ERROR: 'transient'
};

/**
 * Extrahiert den führenden MaStR-Code aus dem `detail`-Feld der Problemantwort
 * (z. B. `"MASTR_NOT_FOUND: ..."` → `"MASTR_NOT_FOUND"`). Liefert `null`, wenn kein bekannter
 * Code vorangestellt ist.
 */
export function mastrErrorCodeFromDetail(detail: string | null | undefined): MastrErrorCode | null {
  return errorCodeFromDetail(detail, Object.keys(MASTR_ERROR_CATEGORIES) as MastrErrorCode[]);
}

/**
 * Ermittelt den anzuzeigenden Hinweis aus Fehler-Code (aus dem `detail`-Feld), HTTP-Status und der
 * Rolle des angemeldeten Benutzers (`isInstaller`).
 */
export function resolveMastrErrorHint(
  code: MastrErrorCode | null | undefined,
  status: number | null | undefined,
  isInstaller: boolean
): MastrErrorHint {
  switch (categorizeRegisterError(code ? MASTR_ERROR_CATEGORIES[code] : null, status)) {
    case 'notFound':
      return {
        title: 'MaStR-Nummer nicht gefunden',
        message:
          'Die angegebene MaStR-Nummer wurde im Marktstammdatenregister nicht gefunden. Bitte überprüfen Sie die Nummer.'
      };
    case 'invalidInput':
      return {
        title: 'Ungültige MaStR-Nummer',
        message: 'Die eingegebene MaStR-Nummer ist ungültig. Bitte überprüfen Sie die Eingabe.'
      };
    case 'configuration':
      return {
        title: 'MaStR-Anbindung nicht verfügbar',
        message: 'Die Anbindung an das Marktstammdatenregister ist derzeit nicht korrekt konfiguriert.',
        hint: contactHint(isInstaller)
      };
    case 'transient':
      return {
        title: 'Marktstammdatenregister zurzeit nicht erreichbar',
        message:
          'Die Daten konnten nicht vom Marktstammdatenregister abgerufen werden. Bitte versuchen Sie es später erneut.',
        hint: persistHint(isInstaller)
      };
    default:
      return {
        title: 'Fehler beim Abrufen der Daten',
        message: 'Die Daten konnten nicht vom Marktstammdatenregister abgerufen werden.'
      };
  }
}
